import React, { useEffect, useState } from 'react';
import { MapPin, X, ExternalLink, RefreshCw, Check } from 'lucide-react';
import { visitsApi, customerLocationApi } from '../services/api';
import { useToast } from './Toast/Toast';

/**
 * Set a customer's position by pasting a Google Maps link.
 *
 * The office looks the shop up on Google, copies the link, and pastes it here —
 * instead of a rep writing coordinates on paper before setting out. The saved
 * position does one job: ordering the route nearest-first.
 *
 * It is deliberately NOT the check-in anchor. That one is written only by a real
 * check-in with real GPS, which is what lets it stand as evidence someone was
 * actually at the shop; a link pasted from a desk cannot carry that weight, and
 * one a few hundred metres out would lock the whole team out of checking in.
 * Both positions are shown here so it is obvious which is which.
 */

/**
 * Addressed either by the visit it hangs off, or by the customer directly —
 * the customer reports have no visit to go through. Exactly one is required.
 */
type Props = {
    customerName?: string;
    onClose: () => void;
    /** Called after a successful save, so the caller can re-sort its list. */
    onSaved?: () => void;
} & ({ visitId: number; customerId?: never } | { customerId: number; visitId?: never });

type Loaded = Awaited<ReturnType<typeof visitsApi.getLocation>>;

const SOURCE_NOTE: Record<string, string> = {
    manual: 'Pasted in by hand',
    google: 'Looked up from the address (Google)',
    osm: 'Looked up from the address (OpenStreetMap)',
};

const PRECISION_NOTE: Record<string, string> = {
    street: 'street level',
    locality: 'locality level',
    pincode: 'pincode area only',
    city: 'city centre only',
};

const VisitLocationModal: React.FC<Props> = ({ visitId, customerId, customerName, onClose, onSaved }) => {
    const { showError, showSuccess } = useToast();
    const [data, setData] = useState<Loaded | null>(null);
    const [loading, setLoading] = useState(true);
    const [link, setLink] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let alive = true;
        const load = visitId !== undefined
            ? visitsApi.getLocation(visitId)
            : customerLocationApi.get(customerId as number);
        load
            .then(d => { if (alive) setData(d); })
            .catch(e => { if (alive) showError('Could not load location', e?.message || 'Unknown error'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visitId, customerId]);

    const save = async () => {
        if (!link.trim()) { showError('Nothing to save', 'Paste a Google Maps link first'); return; }
        setSaving(true);
        try {
            const res: any = visitId !== undefined
                ? await visitsApi.setLocation(visitId, link.trim())
                : await customerLocationApi.set(customerId as number, link.trim());
            setData(res);
            setLink('');
            showSuccess('Location saved', `${res?.map?.lat?.toFixed(5)}, ${res?.map?.lng?.toFixed(5)} — this customer will now be sorted into the route`);
            onSaved?.();
        } catch (e: any) {
            showError('Could not read that link', e?.message || 'Unknown error');
        } finally {
            setSaving(false);
        }
    };

    const name = data?.customer_name || customerName || 'this customer';
    // What the office person clicks to go and find the shop in the first place.
    const searchUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        [name, data?.address].filter(Boolean).join(', '),
    )}`;

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
            <div
                className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white rounded-t-2xl">
                    <div className="flex items-center gap-2 min-w-0">
                        <MapPin className="h-5 w-5 text-blue-600 shrink-0" />
                        <div className="min-w-0">
                            <h3 className="font-bold text-gray-900 truncate">Map location</h3>
                            <p className="text-xs text-gray-500 truncate">{name}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 shrink-0">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {loading ? (
                    <div className="p-10 text-center text-gray-400">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 opacity-40" />
                        <p className="text-sm font-semibold">Loading…</p>
                    </div>
                ) : (
                    <div className="p-5 space-y-4">
                        {data?.address && (
                            <div className="text-xs text-gray-500">
                                <span className="font-bold uppercase tracking-wider text-[10px] text-gray-400 block mb-0.5">Address on file</span>
                                {data.address}
                            </div>
                        )}

                        <div className="rounded-xl border border-gray-200 p-3 bg-gray-50">
                            <span className="font-bold uppercase tracking-wider text-[10px] text-gray-400 block mb-1">Sorting position</span>
                            {data?.map ? (
                                <div className="space-y-1">
                                    <div className="font-mono text-sm font-semibold text-gray-800">
                                        {data.map.lat.toFixed(6)}, {data.map.lng.toFixed(6)}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {SOURCE_NOTE[data.map.source || ''] || 'Saved'}
                                        {data.map.precision && data.map.source !== 'manual' && ` — ${PRECISION_NOTE[data.map.precision] || data.map.precision}`}
                                    </div>
                                    <a
                                        href={`https://www.google.com/maps?q=${data.map.lat},${data.map.lng}`}
                                        target="_blank" rel="noopener noreferrer"
                                        className="text-xs text-blue-600 font-semibold hover:underline inline-flex items-center gap-1"
                                    >
                                        View this pin <ExternalLink className="h-3 w-3" />
                                    </a>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400">
                                    Not set — this customer is pushed to the end of the route instead of being sorted.
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <label className="font-bold uppercase tracking-wider text-[10px] text-gray-400 block">
                                Paste a Google Maps link
                            </label>
                            <a
                                href={searchUrl} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-blue-600 font-semibold hover:underline inline-flex items-center gap-1"
                            >
                                Find "{name}" on Google Maps <ExternalLink className="h-3 w-3" />
                            </a>
                            <input
                                value={link}
                                onChange={e => setLink(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') save(); }}
                                placeholder="https://maps.app.goo.gl/…  or  26.1806, 91.7539"
                                className="w-full px-3 py-2.5 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                            />
                            <p className="text-[11px] text-gray-400 leading-relaxed">
                                In Google Maps, find the shop, then <strong>Share → Copy link</strong> and paste it here.
                                Short <code>maps.app.goo.gl</code> links work too.
                            </p>
                            <button
                                onClick={save}
                                disabled={saving || !link.trim()}
                                className="w-full py-2.5 bg-blue-600 text-white rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                {saving ? 'Saving…' : 'Save location'}
                            </button>
                        </div>

                        {/* Said plainly, because the two positions are easy to confuse and
                            getting them mixed up is what would break check-in. */}
                        <div className="text-[11px] text-gray-500 bg-blue-50 border border-blue-100 rounded-xl p-3 leading-relaxed">
                            This position is used <strong>only to sort the route</strong> nearest-first.
                            It does not affect check-in:{' '}
                            {data?.checkin
                                ? 'this customer already has a check-in location, recorded on site by GPS, and that is what the 200m check-in rule uses.'
                                : 'the check-in location is still recorded automatically the first time someone checks in here.'}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VisitLocationModal;
